import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Box, Container, Typography, Stack } from '@mui/material';
import toaster from 'react-hot-toast';
import DataCard from 'components/common/card/DataCard';
import Modal from 'components/common/Modal';
import IndividualValueEditForm from 'components/common/forms/IndividualValueEditForm';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import {
  fetchIndividualDevice,
  updateIndividualDevice,
  getListOfPossibleDeviceStatuses,
  retriggerInitialization,
} from 'api/admin/devices/devices';
import DeviceSimulator from 'components/admin/deviceDashboard/deviceEditor/simulator/DeviceSimulator';
import styles from 'components/admin/deviceDashboard/deviceEditor/DeviceEditor.module.scss';
import { DEVICE_CONFIG_STATUSES } from 'utils/constants';

const DeviceEditor = () => {
  let { id } = useParams();
  const queryClient = useQueryClient();

  const [modalType, setModalType] = useState();
  const [showModal, setShowModal] = useState(false);
  const [updatedFormKey, setUpdatedFormKey] = useState();
  const [updatedFormValue, setUpdatedFormValue] = useState();
  const [error, setError] = useState(null);
  const [showSimulatorForm, setShowSimulatorForm] = useState(false);

  const { data: device, isLoading } = useQuery(
    ['fetchIndividualDevice', { id }],
    async () => {
      const { data } = await fetchIndividualDevice(id);
      return data.device;
    },
    {
      onError: (error) => {
        setError(error?.response?.data?.error);
      },
    },
  );

  const { data: statuses } = useQuery(
    ['getListOfPossibleDeviceStatuses'],
    async () => {
      const { data } = await getListOfPossibleDeviceStatuses();
      return data.statuses;
    },
    {
      onError: (error) => {
        setError(error?.response?.data?.error);
      },
    },
  );

  /**
   * Set the form value to be updated for the Store
   *
   * @param {Any} value
   */
  const handleDeviceValueChange = (value) => {
    setError(null);
    setUpdatedFormValue(value);
  };

  const { isLoading: isUpdateLoading, mutate: saveUpdatesForDevice } = useMutation({
    mutationFn: ({ id, key, value }) => {
      setShowModal(false);
      return updateIndividualDevice(id, {
        key,
        value,
      });
    },
    onSuccess: () => {
      setModalType(null);
      setUpdatedFormKey(null);
      queryClient.invalidateQueries({ queryKey: ['fetchIndividualDevice', { id }] });
    },
    onError: (error) => {
      setShowModal(true);
      setError(error?.response?.data?.error);
    },
  });

  /**
   * Reinitialize a device and machine pairing
   */
  const { isLoading: isLoadingInitialization, mutate: reinitializeDeviceAndMachine } = useMutation({
    mutationFn: () => {
      return retriggerInitialization(device.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetchIndividualDevice', { id }] });
      toaster('The machine has been successfully re-initialized!', {
        icon: '✅',
      });
    },
    onError: (error) => {
      toaster(`Reinitializing device error: ${error?.response?.data?.error}`);
      setError(error?.response?.data?.error);
    },
  });

  /**
   * function that handles mutation for editing a device
   */
  const saveDevice = () => {
    saveUpdatesForDevice({
      id: device.id,
      key: updatedFormKey,
      value: updatedFormValue,
    });
  };

  /**
   * Show the edit-details modal for updating device properties
   */
  const renderModal = () => {
    const currentValue = device[modalType];

    return (
      <Modal
        onClose={() => {
          setModalType(null);
          setUpdatedFormKey(null);
          setShowModal(false);
        }}
      >
        <IndividualValueEditForm
          modalType={modalType}
          currentValue={currentValue}
          updatedValue={updatedFormValue}
          handleValueChange={(event) => {
            handleDeviceValueChange(event.value);
          }}
          inputType="select"
          selectOptions={statuses}
          onSave={saveDevice}
          error={error}
        />
      </Modal>
    );
  };

  /**
   * When clicking "simulate device" show the proper form/wizards
   */
  const handleSimulateDeviceButton = () => {
    setShowSimulatorForm(true);
  };

  /**
   * handle boolean toggle functionality for device properties
   *
   * @param {string} key
   * @param {boolean} value
   */
  const handleDeviceToggle = (key, value) => {
    saveUpdatesForDevice({
      id: device.id,
      key,
      value,
    });
  };

  /**
   * determine the details to display and edit for the device
   */
  const displayDeviceDetails = () => {
    const deviceDetails = [
      {
        label: 'ID',
        data: device.id,
      },
      {
        label: 'Device MAC Address',
        data: device.name,
      },
      {
        label: 'Device Status',
        data: device.status,
        editable: true,
        rawLabel: 'status',
      },
      {
        label: 'Upper Status (stacked-dryer)',
        data: device.upperStatus ?? 'N/A - Not Stacked',
        editable: !!device.stackedType,
        rawLabel: 'upperStatus',
      },
      {
        label: 'Simulated',
        data: device.simulated,
        toggleable: !['IN_USE', 'PENDING_START'].includes(device.status),
        rawLabel: 'simulated',
      },
      {
        label: 'Machine Name',
        data: device.isPaired || device.machineName ? device.machineName : 'N/A - Not Paired',
      },
      {
        label: 'Store Name',
        data: device.storeName,
      },
      {
        label: 'Business Name',
        data: device.businessName,
      },
    ];

    return (
      <DataCard
        title={'Device Details'}
        cardData={deviceDetails}
        onEdit={(modalType) => {
          setModalType(modalType);
          setUpdatedFormKey(modalType);
          setShowModal(true);
        }}
        onToggle={(field, value) => {
          handleDeviceToggle(field, value);
        }}
      />
    );
  };

  /**
   * Fetch the latest details of the device and hide the simulation wizard
   */
  const hideDeviceSimulatorForm = () => {
    queryClient.invalidateQueries({ queryKey: ['fetchIndividualDevice', { id: device.id }] });
    setShowSimulatorForm(false);
  }

  return (
    <>
      {showSimulatorForm ? (
        <DeviceSimulator
          device={device}
          onBack={hideDeviceSimulatorForm}
        />
      ) : (
        <Container maxWidth={false} sx={{ height: '100vh' }}>
          <Box p={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h4" mb={3}>
                Device Details
              </Typography>
              {(device?.simulated) && (
                <button className={styles.simulateDeviceButton} onClick={handleSimulateDeviceButton}>
                  Simulate Device
                </button>
              )}
              {(!device?.simulated && device?.configStatus === DEVICE_CONFIG_STATUSES.CONFIGURED) && (
                <button className={styles.simulateDeviceButton} onClick={reinitializeDeviceAndMachine} disabled={isLoadingInitialization}>
                  Re-Initialize Machine
                </button>
              )}
            </Stack>

            {(isLoading || isUpdateLoading || isLoadingInitialization) ? <CentsLoader /> : displayDeviceDetails()}
            {showModal && renderModal()}
          </Box>
        </Container>
      )}
    </>
  );
};

export default DeviceEditor;
